import { useMemo, useState } from 'react';
import classNames from 'classnames';
import classes from './ReviewQualifiedRealPlay.module.scss';
import sparkle from 'assets/icons/sparkle.svg';
import { CircularProgress } from '@material-ui/core';
import RatingCriteriaTooltip from 'features/roleplay/Components/Common/RatingCriteriaTooltip';
import RoleplayRating from 'features/roleplay/Components/Common/RoleplayRating/RoleplayRating';
import { CustomTooltipWhite, CustomButton, TextArea } from 'shared_ui_components';
import AIFeedback from 'shared_ui_components/AIFeatures/AIFeedback/AIFeedback';
import { Skeleton, Rating } from '@material-ui/lab';
import { Collapse, withStyles } from '@material-ui/core';
import RealPlayRecordDataHelper from 'features/realplays/RealPlayRecordData.helper';
import RealPlayQualifiedDataHelper from 'features/realplays/RealPlayQualifiedData.helper';
import VideoPlayer from '../Common/VideoPlayer';

const RealPlayLeftSection = ({ isLoading, loadingScenarioResponseInfo }) => {
  const [showRatingDetails, setShowRatingDetails] = useState(false);
  const { videoURL, selectedScenario } = RealPlayRecordDataHelper();

  const {
    rating_criteria,
    feedback,
    status_ai,
    isEditing,
    setIsEditing,
    setFeedback,
    setRatingCriteria,
    setDiscardChanges,
  } = RealPlayQualifiedDataHelper();

  const ratingHasAISuggestions = useMemo(() => {
    return rating_criteria?.every(({ reasoning, improvement_suggestion }) => {
      return reasoning || improvement_suggestion;
    });
  }, [rating_criteria]);

  const overallRating = useMemo(() => {
    if (!rating_criteria || rating_criteria?.length === 0) return 0;
    const initialValue = 0;
    const sum = rating_criteria.reduce(
      (accumulator, currentValue) => accumulator + currentValue?.rating,
      initialValue
    );
    let avg = sum / rating_criteria?.length;

    return sum === 0 ? 0 : avg;
  }, [rating_criteria]);

  const openRatingDetails = () => setShowRatingDetails(true);
  const closeRatingDetails = () => setShowRatingDetails(false);

  const editRating = () => setIsEditing(true);
  const saveChanges = () => setIsEditing(false);
  const discardChanges = () => {
    setDiscardChanges();
    setIsEditing(false);
  };
  const handleEditStar = (newRating, Uuid) => setRatingCriteria(newRating, Uuid);

  const handleChange = (value) => setFeedback(value);

  const StyledRating = withStyles({
    iconFilled: {
      color: '#EB9B42',
    },
    iconEmpty: {
      color: '#F9E1C6',
    },
  })(Rating);

  return (
    <div className={classes.reviewContainer}>
      {/* VIDEO*/}
      {isLoading ? (
        <Skeleton variant="rect" width="100%" height={190} />
      ) : (
        videoURL && <VideoPlayer url={videoURL} />
      )}
      {/* AI banner */}
      {!isEditing && (
        <div className={classes.ai_active}>
          <div>
            <div>
              <img src={sparkle} alt="sparkle" />
              {isLoading || loadingScenarioResponseInfo ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <div>
                  <span>The fields were filled in automatically using AI </span>
                  {selectedScenario?.response?.status === null && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <div>Adjust the rating as needed</div>
                      <div
                        style={{
                          cursor: 'pointer',
                          color: '#0494FD',
                        }}
                        className={classes.action}
                        onClick={editRating}
                      >
                        <span>Edit</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rating criteria and stars given */}
      <div className={classes.starsContainer}>
        <div className={classes.headContainer}>
          <RatingCriteriaTooltip />
          {ratingHasAISuggestions && (
            <div
              className={classes.action}
              onClick={showRatingDetails ? closeRatingDetails : openRatingDetails}
            >
              {showRatingDetails ? 'Hide' : 'Show'} details
            </div>
          )}
        </div>
        <div className={classNames([classes.globalContainer, showRatingDetails && classes.gap])}>
          {isLoading || loadingScenarioResponseInfo ? (
            <CircularProgress size={30} color="inherit" />
          ) : (
            rating_criteria?.map((c) => (
              <Collapse
                in={showRatingDetails}
                collapsedSize={25}
                key={c?.uuid}
                className={classNames([showRatingDetails && classes.collapseSuggestions])}
              >
                <div className={classes.criteriaContainer}>
                  <b className="heading6">{c?.name}</b>
                  {isEditing ? (
                    <StyledRating
                      name={`Edit-${c?.name}`}
                      value={c?.rating}
                      onChange={(event, newValue) => {
                        handleEditStar(newValue, c?.uuid);
                      }}
                    />
                  ) : (
                    <RoleplayRating withTooltip stars={status_ai === 'SUCCEEDED' ? c?.rating : 0} />
                  )}
                </div>

                {/* AI reasoning and suggestions */}
                {showRatingDetails && (
                  <div>
                    <p className="heading7Bold" style={{ marginTop: '0.25rem' }}>
                      {c.reasoning ? 'Why this rating?' : 'AI "Why this rating?" is not available'}
                    </p>
                    {c.reasoning && (
                      <p className="heading7" style={{ marginTop: '0.25rem' }}>
                        {c.reasoning}
                      </p>
                    )}
                  </div>
                )}

                {showRatingDetails && (
                  <div>
                    <p className="heading7Bold" style={{ marginTop: '0.25rem' }}>
                      {c.improvement_suggestion
                        ? 'How to improve?'
                        : 'AI "How to improve?" is not available'}
                    </p>
                    {c?.improvement_suggestion && (
                      <p className="heading7" style={{ marginTop: '0.25rem' }}>
                        {c.improvement_suggestion}
                      </p>
                    )}
                  </div>
                )}
              </Collapse>
            ))
          )}
        </div>

        <hr />
        {!isEditing && (
          <div className={classes.criteriaContainer}>
            <b className="heading6">Overall Score</b>
            {isLoading || loadingScenarioResponseInfo ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <RoleplayRating
                withTooltip
                stars={status_ai === 'SUCCEEDED' ? overallRating : 0}
                overallScore
              />
            )}
          </div>
        )}
      </div>

      {/* Comment given by the manager or AI*/}
      <div>
        <div className={classes.feedbackTitle}>
          <span>Feedback:</span>
          <CustomTooltipWhite
            tooltipIcon="info_icon.svg"
            title="Rating and feedback has been generated by AI."
          />
        </div>
        <div className="inputGroup textAreaGroup">
          {isLoading || loadingScenarioResponseInfo ? (
            <CircularProgress size={18} color="inherit" />
          ) : isEditing ? (
            <TextArea rows={3} value={feedback} onChange={(e) => handleChange(e.target.value)} />
          ) : (
            <AIFeedback ai_feedback_status={status_ai} ai_feedback={feedback} assessment />
          )}
        </div>
      </div>

      {/*In case of edition */}
      {isEditing && (
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <CustomButton
            className="secondaryBtn"
            onClick={discardChanges}
            style={{ color: '#E83A3A', border: '1px solid #E83A3A' }}
          >
            Discard
          </CustomButton>
          <CustomButton onClick={saveChanges} style={{ backgroundColor: '#2DC38D' }}>
            Save
          </CustomButton>
        </div>
      )}
    </div>
  );
};

export default RealPlayLeftSection;
